import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Copy, Download, Film, Image as ImageIcon, Maximize2, MoreHorizontal, Move3d, PenLine, RefreshCw, Sparkles, Trash2, Video } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';

import { StudioFormat, StudioShot } from '../studio-contract';
import { aspectValue, cameraLabel, moveLabel } from '../labels';
import { TextPreview } from './TextPreview';
import { GraphicsPreview } from './GraphicsPreview';
import { graphicsOf } from '../graphics';
import { isTakeShot, photoIsDesigned } from '../studio-contract';
import { clipIn3d } from '../surface-shots';
import { ANGLE_LIST, AnglePreset, EDITOR_TOOLS, EditorTool, angleLabel, editorToolLabel } from '../edit-actions';

import { SELECTED_FRAME } from '../../../../components/design-system/common/selection';

/** A small label over a picture: dark glass, readable on light and dark photos alike. */
const GLASS = 'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-black/45 px-1.5 text-[10.5px] font-medium leading-none text-white backdrop-blur-md';

export interface ProductInfo {
    uuid: string;
    name: string;
    thumbnailUrl?: string;
    visualizationType?: string;
    /** Top-level category slug, e.g. 'rugs'. */
    category?: string;
    /** Set when the product has a 3D model — the shot can then be posed in the 3D studio. */
    modelPath?: string;
    /** The product's own picture at full size (the thumbnail is small and cropped). */
    imageUrl?: string;
}

/** A catalogue product (as the pickers and search return it) as the board needs it. */
export function productInfoFrom(p: any): ProductInfo {
    return {
        uuid: p.uuid,
        name: typeof p.name === 'string' ? p.name : (p.name?.en || Object.values(p.name || {})[0] || ''),
        thumbnailUrl: p.thumbnailPath || p.originalImagePath,
        imageUrl: p.originalImagePath || p.thumbnailPath,
        visualizationType: p.visualizationType,
        category: p.category,
        modelPath: p.modelPath,
    };
}

interface ShotCardProps {
    shot: StudioShot;
    index: number;
    /** This shot's photo is the key photo other shots are made from. */
    isKey?: boolean;
    /** The number of the shot whose key photo this one is made from. */
    fromShot?: number;
    format: StudioFormat;
    selected: boolean;
    products: Map<string, ProductInfo>;
    stillPrice: number | null;
    clipPrice: number | null;
    onSelect: () => void;
    onMakeStill: () => void;
    onMakeClip: () => void;
    /** A finished 3D clip filmed for real (tiles shot as objects). */
    onMakeReal?: () => void;
    realPrice?: number | null;
    onDuplicate: () => void;
    onDelete: () => void;
    /** Open the shot at full size. */
    onPreview: () => void;
    onEditTool: (tool: EditorTool) => void;
    onDesign: () => void;
    onAngle: (preset: AnglePreset) => void;
    onUpscale: () => void;
    /** The vendor's brand, for logo parts. */
    brand?: { logoUrl?: string; name?: string };
}

/**
 * One shot on the board.
 *
 * The card shows the shot as it will be published: the picture (or the clip,
 * playing) at the board's format, with its text laid over it. Its one primary
 * button is always the next step — make the photo, then film it — with the
 * price on the button, so nothing is spent by surprise.
 */
export const ShotCard: React.FC<ShotCardProps> = ({
    shot, index, isKey, fromShot, format, selected, products, stillPrice, clipPrice,
    onSelect, onMakeStill, onMakeClip, onMakeReal, realPrice, onDuplicate, onDelete, onPreview, onEditTool, onDesign, onAngle, onUpscale, brand,
}) => {
    const { t } = useTranslation();
    const stillPending = shot.still?.status === 'pending';
    const stillWaiting = shot.still?.status === 'waiting';
    const clipPending = shot.clip?.status === 'pending';
    const stillReady = shot.still?.status === 'ready' && !!shot.still.url;
    const clipReady = shot.clip?.status === 'ready' && !!shot.clip.url;
    const failed = shot.clip?.status === 'failed' ? shot.clip : (shot.still?.status === 'failed' || shot.still?.error) ? shot.still : null;
    const hero = shot.productUuids[0] ? products.get(shot.productUuids[0]) : undefined;
    const cam = cameraLabel(t, shot.camera);

    const filming3d = shot.clip?.status === 'waiting' && shot.clip.waitingFor === 'capture';
    const isCard = shot.kind === 'card';
    const parts = graphicsOf(shot);
    // The words the film (or a card) will carry; a photo shows them once they are drawn on it.
    const showParts = parts.length > 0 && (isCard || shot.kind === 'video' || (shot.kind === 'image' && !photoIsDesigned(shot)));
    const next: { label: string; price: number | null; run: () => void; icon: React.ReactNode } | null = isCard || stillPending || clipPending || stillWaiting || filming3d
        ? null
        : clipIn3d(shot) && !clipReady
            ? { label: t('STUDIO.SHOT.RENDER_3D', 'Render in 3D'), price: null, run: onMakeClip, icon: <Film className="size-3.5" /> }
        : isTakeShot(shot) && !clipReady
            ? { label: t('STUDIO.TAKE.MAKE_REAL', 'Make it real'), price: clipPrice, run: onMakeClip, icon: <Sparkles className="size-3.5" /> }
        : clipIn3d(shot) && clipReady && !isTakeShot(shot) && onMakeReal
            ? { label: t('STUDIO.TAKE.MAKE_REAL', 'Make it real'), price: realPrice ?? null, run: onMakeReal, icon: <Sparkles className="size-3.5" /> }
        : !stillReady
            ? { label: t('STUDIO.SHOT.MAKE_PHOTO', 'Make photo'), price: stillPrice, run: onMakeStill, icon: <Sparkles className="size-3.5" /> }
            : shot.kind === 'video' && !clipReady
                ? { label: t('STUDIO.SHOT.ANIMATE', 'Film it'), price: clipPrice, run: onMakeClip, icon: <Film className="size-3.5" /> }
                : null;

    const downloadUrl = clipReady ? shot.clip!.url : stillReady ? shot.still!.url : undefined;

    return (
        <div
            data-shot-card
            className={cn(
                'group flex flex-col overflow-hidden rounded-2xl border bg-card transition-[border-color,box-shadow]',
                selected ? SELECTED_FRAME : 'border-border hover:border-foreground/30',
            )}
        >
            <div className="relative">
            <button type="button" onClick={onSelect} onDoubleClick={() => (stillReady || clipReady) && onPreview()} className="relative block w-full cursor-pointer bg-muted" style={{ aspectRatio: String(aspectValue(format)) }}>
                {isCard ? (
                    <div className="absolute inset-0" style={{ background: shot.background || '#0B0B0B' }} />
                ) : clipReady ? (
                    <video src={shot.clip!.url} poster={shot.still?.url} muted loop autoPlay playsInline className="absolute inset-0 size-full object-cover" />
                ) : !shot.still?.url && isTakeShot(shot) ? (
                    <video src={shot.take3d!.url} poster={shot.take3d!.posterUrl} muted loop autoPlay playsInline className="absolute inset-0 size-full object-cover opacity-90" />
                ) : shot.still?.url ? (
                    <img src={shot.still.url} alt={shot.title || cam.label} className={cn('absolute inset-0 size-full object-cover', stillPending && 'opacity-40')} />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
                        {hero?.thumbnailUrl
                            ? <img src={hero.thumbnailUrl} alt="" className="size-14 rounded-lg object-cover opacity-80" />
                            : <ImageIcon className="size-6 text-muted-foreground" />}
                        <span className="line-clamp-3 text-xs text-muted-foreground">{shot.scene || t('STUDIO.SHOT.NO_SCENE', 'Describe the scene')}</span>
                    </div>
                )}

                {showParts && <GraphicsPreview graphics={parts} logoUrl={brand?.logoUrl} brandName={brand?.name} />}
                {!showParts && shot.text?.content && (clipReady || (stillReady && !photoIsDesigned(shot))) && <TextPreview text={shot.text} />}

                <span className="absolute inset-x-2 top-2 flex items-start gap-1">
                    <span className={GLASS}>{index + 1}</span>
                    {isKey && (
                        <span className={GLASS} title={t('STUDIO.SHOT.KEY_HINT', 'The other shots of this set are made from this photo, so the product and the room stay the same.')}>
                            {shot.inFilm === false ? t('STUDIO.SHOT.KEY_ONLY', 'Key photo · not in the film') : t('STUDIO.SHOT.KEY', 'Key photo')}
                        </span>
                    )}
                    {fromShot !== undefined && (
                        <span className={GLASS} title={t('STUDIO.SHOT.FROM_HINT', 'Made from that shot\'s photo: same product, same room — only the camera and the light change.')}>
                            {t('STUDIO.SHOT.FROM', 'from {{n}}', { n: fromShot })}
                        </span>
                    )}
                    {isCard && <span className={cn(GLASS, 'ms-auto')}>{t('STUDIO.SHOT.CARD', 'Card')}</span>}
                    {shot.kind === 'video' && (
                        <span className={cn(GLASS, 'ms-auto gap-1')} title={t('STUDIO.SHOT.VIDEO', 'Video')}>
                            <Video className="size-3" />{`${Math.round((shot.durationSec || 4) * 10) / 10}s`}
                        </span>
                    )}
                </span>

                {filming3d && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/40 backdrop-blur-[2px]">
                        <Spinner className="size-5" />
                        <span className="rounded-md bg-background/90 px-2 py-0.5 text-xs text-foreground">{t('STUDIO.SHOT.FILMING_3D', 'Filming the tiles in 3D…')}</span>
                    </div>
                )}
                {stillWaiting && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/30 p-3">
                        <span className="rounded-md bg-background/90 px-2 py-0.5 text-center text-xs text-foreground">
                            {t('STUDIO.SHOT.WAITING', 'Waiting for the key photo…')}
                        </span>
                    </div>
                )}
                {(stillPending || clipPending) && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/40 backdrop-blur-[2px]">
                        <Spinner className="size-5" />
                        <span className="rounded-md bg-background/90 px-2 py-0.5 text-xs text-foreground">
                            {clipPending ? t('STUDIO.SHOT.FILMING', 'Filming…') : t('STUDIO.SHOT.SHOOTING', 'Taking the photo…')}
                        </span>
                    </div>
                )}
                {failed && !stillPending && !clipPending && (
                    <div className="absolute inset-x-2 bottom-2 flex items-start gap-1.5 rounded-md bg-background/95 p-2 text-left text-xs text-foreground">
                        <AlertCircle className="mt-px size-3.5 shrink-0 text-destructive" />
                        <span className="line-clamp-2">{failed.error || t('STUDIO.SHOT.FAILED', 'It did not work this time')}</span>
                    </div>
                )}
            </button>
            {(stillReady || clipReady) && (
                <button type="button" onClick={onPreview} aria-label={t('STUDIO.PREVIEW.OPEN', 'View full size')} title={t('STUDIO.PREVIEW.OPEN', 'View full size')}
                    className="absolute bottom-2 right-2 flex size-8 cursor-pointer items-center justify-center rounded-md bg-background/90 text-foreground opacity-0 shadow-sm backdrop-blur transition-opacity group-hover:opacity-100 focus-visible:opacity-100">
                    <Maximize2 className="size-4" />
                </button>
            )}
            </div>

            <div className="space-y-2 px-3 pb-3 pt-2.5">
                <div className="flex items-start gap-1">
                <button type="button" onClick={onSelect} className="min-w-0 flex-1 cursor-pointer text-left">
                    <div className="truncate text-sm font-medium text-foreground">{shot.title || (isCard ? t('STUDIO.SHOT.CARD', 'Card') : cam.label)}</div>
                    <div className="truncate text-xs text-muted-foreground">
                        {isCard ? t('STUDIO.SHOT.CARD_HINT', 'Title or end card') : <>{hero?.name ? `${hero.name} · ` : ''}{cam.label}{shot.kind === 'video' && shot.motion ? ` · ${moveLabel(t, shot.motion).label}` : ''}</>}
                    </div>
                </button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={t('STUDIO.SHOT.MORE', 'More')}>
                            <MoreHorizontal className="size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem disabled={stillPending} onSelect={onMakeStill}>
                            <RefreshCw className="size-4" />{stillReady ? t('STUDIO.SHOT.NEW_TAKE', 'New photo take') : t('STUDIO.SHOT.MAKE_PHOTO', 'Make photo')}
                            {stillPrice != null && <span className="ml-auto text-xs text-muted-foreground">{stillPrice}</span>}
                        </DropdownMenuItem>
                        {stillReady && (
                            <DropdownMenuItem disabled={clipPending} onSelect={onMakeClip}>
                                <Film className="size-4" />{clipReady ? t('STUDIO.SHOT.NEW_CLIP', 'Film it again') : t('STUDIO.SHOT.ANIMATE', 'Film it')}
                                {clipPrice != null && <span className="ml-auto text-xs text-muted-foreground">{clipPrice}</span>}
                            </DropdownMenuItem>
                        )}
                        {stillReady && !stillPending && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger><PenLine className="size-4" />{t('STUDIO.EDIT.EDIT_PHOTO', 'Edit photo')}</DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuItem onSelect={onDesign}>{t('STUDIO.EDIT.DESIGN', 'Design: text, logo, layout')}</DropdownMenuItem>
                                        {EDITOR_TOOLS.map((tool) => (
                                            <DropdownMenuItem key={tool} onSelect={() => onEditTool(tool)}>{editorToolLabel(t, tool).label}</DropdownMenuItem>
                                        ))}
                                        <DropdownMenuItem onSelect={onUpscale}>{t('STUDIO.EDIT.UPSCALE', 'Sharpen and enlarge (2×)')}</DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger><Move3d className="size-4" />{t('STUDIO.EDIT.ANGLE', 'Change angle')}</DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        {ANGLE_LIST.map((p) => (
                                            <DropdownMenuItem key={p} onSelect={() => onAngle(p)}>{angleLabel(t, p)}</DropdownMenuItem>
                                        ))}
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {downloadUrl && (
                            <DropdownMenuItem asChild>
                                <a href={downloadUrl} download target="_blank" rel="noreferrer"><Download className="size-4" />{t('STUDIO.SHOT.DOWNLOAD', 'Download')}</a>
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onSelect={onDuplicate}><Copy className="size-4" />{t('STUDIO.SHOT.DUPLICATE', 'Duplicate')}</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive"><Trash2 className="size-4" />{t('STUDIO.SHOT.DELETE', 'Delete shot')}</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                </div>
                {next && (
                    <Button variant="outline" size="sm" onClick={next.run} className="w-full gap-1.5">
                        {next.icon}
                        {next.label}
                        {next.price != null && <span className="text-muted-foreground">· {next.price}</span>}
                    </Button>
                )}
            </div>
        </div>
    );
};
